export interface ArticleTopicHistoryState extends Record<string, unknown> {
	topicReturnSlug: string;
	topicReturnDepth: number;
}

export type TopicReturnDecision =
	| { kind: 'go'; delta: number }
	| { kind: 'replace'; path: string };

interface ArticleHistoryTransitionInput {
	sourceView: string | null;
	topicSlug: string;
	pushesRoute: boolean;
}

export function nextArticleTopicHistoryState(
	currentState: unknown,
	input: ArticleHistoryTransitionInput,
): ArticleTopicHistoryState | Record<string, never> {
	const current = readArticleTopicHistoryState(currentState);
	if (!input.pushesRoute) {
		return current?.topicReturnSlug === input.topicSlug ? current : {};
	}
	if (input.sourceView === 'topic') {
		return { topicReturnSlug: input.topicSlug, topicReturnDepth: 1 };
	}
	if (input.sourceView === 'article' && current?.topicReturnSlug === input.topicSlug) {
		return {
			topicReturnSlug: input.topicSlug,
			topicReturnDepth: current.topicReturnDepth + 1,
		};
	}
	return {};
}

export function resolveBackToTopic(
	state: unknown,
	topicSlug: string,
	historyLength: number,
): TopicReturnDecision {
	const current = readArticleTopicHistoryState(state);
	const availableDepth = Math.max(0, Math.trunc(historyLength) - 1);
	if (
		current?.topicReturnSlug === topicSlug
		&& current.topicReturnDepth <= availableDepth
	) {
		return { kind: 'go', delta: -current.topicReturnDepth };
	}
	return { kind: 'replace', path: `/topics/${encodeURIComponent(topicSlug)}` };
}

function readArticleTopicHistoryState(state: unknown): ArticleTopicHistoryState | null {
	if (!state || typeof state !== 'object') return null;
	const candidate = state as Partial<ArticleTopicHistoryState>;
	if (
		typeof candidate.topicReturnSlug !== 'string'
		|| candidate.topicReturnSlug.length === 0
		|| !Number.isInteger(candidate.topicReturnDepth)
		|| (candidate.topicReturnDepth ?? 0) < 1
	) {
		return null;
	}
	return {
		topicReturnSlug: candidate.topicReturnSlug,
		topicReturnDepth: candidate.topicReturnDepth as number,
	};
}
